// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Layout component registry - UI components for the layout builder.
 *
 * Applications and Subsystems register layout components that appear in the
 * layout palette and can be placed in workspace tabs.
 *
 * - Applications: Domain-specific apps (Contract Review, Legislative Review)
 * - Subsystems: Platform infrastructure (Knowledge Base, Documents)
 */

import type { ComponentType } from 'react'

import type { NodeUserViewProps, NodeAdminViewProps } from './types'
import { DEFAULT_ORDER, DEFAULT_CATEGORY_ORDER, CATEGORY_PRESETS, startCase } from './registry-utils'

export interface LayoutComponentDefinition {
  id: string
  label: string
  icon: string
  category: string
  description?: string
  order?: number
  Component: ComponentType<NodeUserViewProps>
  ConfigComponent?: ComponentType<NodeAdminViewProps>
}

export interface ApplicationRegistration {
  id: string
  name: string
  description?: string
  components: LayoutComponentDefinition[]
}

export interface SubsystemRegistration {
  id: string
  name: string
  description?: string
  components: LayoutComponentDefinition[]
}

export interface LayoutComponentDisplay {
  id: string
  label: string
  description: string
  category: string
  categoryLabel: string
  categoryOrder: number
  icon: string
  order: number
}

export interface RegisteredLayoutComponent {
  id: string
  parentId: string
  parentType: 'application' | 'subsystem'
  Component: ComponentType<NodeUserViewProps>
  ConfigComponent?: ComponentType<NodeAdminViewProps>
  display: LayoutComponentDisplay
}

interface RegisteredApplication extends ApplicationRegistration {
  componentIds: string[]
}

interface RegisteredSubsystem extends SubsystemRegistration {
  componentIds: string[]
}

const applicationRegistry = new Map<string, RegisteredApplication>()
const subsystemRegistry = new Map<string, RegisteredSubsystem>()
const layoutComponentRegistry = new Map<string, RegisteredLayoutComponent>()

function normalizeDisplay(def: LayoutComponentDefinition): LayoutComponentDisplay {
  const preset = CATEGORY_PRESETS[def.category]

  return {
    id: def.id,
    label: def.label,
    description: def.description ?? '',
    category: def.category,
    categoryLabel: preset?.label ?? startCase(def.category),
    categoryOrder: preset?.order ?? DEFAULT_CATEGORY_ORDER,
    icon: def.icon,
    order: def.order ?? DEFAULT_ORDER,
  }
}

function registerComponents(
  parentId: string,
  parentType: 'application' | 'subsystem',
  components: LayoutComponentDefinition[]
): string[] {
  const ids: string[] = []

  for (const def of components) {
    if (!def.id || !def.label || !def.icon || !def.category || !def.Component) {
      throw new Error(`Layout component in ${parentId}: id, label, icon, category, and Component are required`)
    }

    if (layoutComponentRegistry.has(def.id)) {
      throw new Error(`Layout component "${def.id}" is already registered`)
    }

    layoutComponentRegistry.set(def.id, {
      id: def.id,
      parentId,
      parentType,
      Component: def.Component,
      ConfigComponent: def.ConfigComponent,
      display: normalizeDisplay(def),
    })

    ids.push(def.id)
  }

  return ids
}

export function registerApplication(registration: ApplicationRegistration): void {
  const { id, components } = registration

  if (!id || !registration.name || components.length === 0) {
    throw new Error(`Application "${id}": id, name, and at least one component are required`)
  }

  if (applicationRegistry.has(id)) {
    throw new Error(`Application "${id}" is already registered`)
  }

  const componentIds = registerComponents(id, 'application', components)
  applicationRegistry.set(id, { ...registration, componentIds })
}

export function registerSubsystem(registration: SubsystemRegistration): void {
  const { id, components } = registration

  if (!id || !registration.name || components.length === 0) {
    throw new Error(`Subsystem "${id}": id, name, and at least one component are required`)
  }

  if (subsystemRegistry.has(id)) {
    throw new Error(`Subsystem "${id}" is already registered`)
  }

  const componentIds = registerComponents(id, 'subsystem', components)
  subsystemRegistry.set(id, { ...registration, componentIds })
}

export function getLayoutComponent(id: string): RegisteredLayoutComponent | undefined {
  return layoutComponentRegistry.get(id)
}

export function getAllLayoutComponents(): RegisteredLayoutComponent[] {
  return Array.from(layoutComponentRegistry.values())
}

export function resetLayoutRegistry(): void {
  applicationRegistry.clear()
  subsystemRegistry.clear()
  layoutComponentRegistry.clear()
}
